
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { firstValueFrom, timeout } from 'rxjs';
import { Subscription, interval, startWith, switchMap, catchError, of } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';

type FormaPagamento = 'PIX' | 'CARTAO';

interface PlanoCheckout {
  codigo: string;
  nome: string;
  valor: number;
  intervaloMeses: number;
}

interface PedidoCriado {
  id: number | string;
  referencia?: string;
  plano?: string;
  valor?: number;
  status?: string;
}

interface PagamentoPix {
  paymentId?: string;
  status?: string;
  imagemBase64?: string;
  copiaECola?: string;
  expiraEm?: string;
}

interface PagamentoCartao {
  paymentId?: string;
  status?: string;
  invoiceUrl?: string;
}

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css'],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  private readonly apiBaseUrl = `${environment.apiUrl}/checkout`;

  private _mensagemErro = '';
  recorrenciaAceita = false;
  private alertaErroAberto = false;

  get mensagemErro(): string {
    return this._mensagemErro;
  }

  set mensagemErro(valor: string) {
    this._mensagemErro = valor ?? '';

    if (this._mensagemErro.trim() && !this.alertaErroAberto) {
      void this.mostrarErro(this._mensagemErro);
    }
  }

  planos: PlanoCheckout[] = [];
  planoSelecionado: PlanoCheckout | null = null;

  carregandoPlanos = true;
  processando = false;
  buscandoCep = false;

  mensagemSucesso = '';

  formaPagamento: FormaPagamento = 'PIX';
  quantidadeParcelas = 1;

  compradorEhEmergencia = false;
  termosAceitos = false;
  privacidadeAceita = false;
  pixCopiado = false;

  resultadoPix: PagamentoPix | null = null;
  resultadoCartao: PagamentoCartao | null = null;

  imagemQrCodeBase64: string | null = null;
  codigoPix: string | null = null;

  readonly anoAtual = new Date().getFullYear();
  readonly dataMaximaNascimento = new Date().toISOString().slice(0, 10);

  readonly estados = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
  ];

  comprador = {
    nome: '',
    telefone: '',
    email: '',
    cpf: '',
    vinculoUsuario: '',
  };

  usuario = {
    nome: '',
    telefone: '',
    email: '',
    dataNascimento: '',
    possuiLimitacaoFisica: false,
    informacaoMobilidade: '',
  };

  emergencia = {
    nome: '',
    telefone: '',
    email: '',
  };

  endereco = {
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
  };

  cartao = {
    numero: '',
    titular: '',
    validade: '',
    cvv: '',
    cpfCnpjTitular: '',
  };

  pagamentoConfirmado = false;

  pedidoAtualId: number | string | null = null;
  statusPagamentoAtual = 'PENDENTE';
  private monitoramentoPagamento?: Subscription;

  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    void this.carregarPlanos();
  }

  ngOnDestroy(): void {
    this.pararMonitoramentoPagamento();
  }


  private abrirCarregamentoPagamento(): void {
    Swal.fire({
      title: 'Processando pagamento...',
      html: `
      <p style="margin: 0; color: #64748b;">
        Estamos enviando sua solicitação com segurança.
        Por favor, não feche esta página.
      </p>
    `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      showCloseButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  }

  private fecharCarregamentoPagamento(): void {
    if (Swal.isVisible()) {
      Swal.close();
    }
  }


  private pararMonitoramentoPagamento(): void {
    this.monitoramentoPagamento?.unsubscribe();
    this.monitoramentoPagamento = undefined;
  }

  private async mostrarErro(mensagem: string): Promise<void> {
    if (this.alertaErroAberto) {
      return;
    }

    this.alertaErroAberto = true;

    try {
      await Swal.fire({
        icon: 'error',
        title: 'Não foi possível continuar',
        text: mensagem,
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#287b70',
        heightAuto: false,
      });
    } finally {
      this.alertaErroAberto = false;
    }
  }

  private async mostrarSucesso(titulo: string, mensagem: string): Promise<void> {
    await Swal.fire({
      icon: 'success',
      title: titulo,
      text: mensagem,
      confirmButtonText: 'Continuar',
      confirmButtonColor: '#287b70',
      heightAuto: false,
    });
  }

  iniciarMonitoramentoPagamento(pedidoId: number | string): void {
    this.pararMonitoramentoPagamento();

    this.pedidoAtualId = pedidoId;

    this.monitoramentoPagamento = interval(5000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.http
            .get<{ status: string }>(
              `${this.apiBaseUrl}/pedidos/${encodeURIComponent(String(pedidoId))}/status`,
            )
            .pipe(
              catchError((erro) => {
                console.error('Erro ao consultar pagamento:', erro);
                return of(null);
              }),
            ),
        ),
      )
      .subscribe((resposta) => {
        if (!resposta?.status) return;

        const status = resposta.status.trim().toUpperCase();

        this.statusPagamentoAtual = status;

        if (this.resultadoPix) {
          this.resultadoPix.status = status;
        }

        if (this.resultadoCartao) {
          this.resultadoCartao.status = status;
        }

        // Só altera a tela quando o backend confirmar o pagamento.
        const confirmado = ['PAGO', 'RECEIVED', 'CONFIRMED'].includes(status);

        if (confirmado && !this.pagamentoConfirmado) {
          this.pagamentoConfirmado = true;

          this.pararMonitoramentoPagamento();

          this.cdr.detectChanges();

          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }

        // Interrompe a consulta se o pagamento não puder mais prosseguir.
        if (['VENCIDO', 'CANCELADO', 'CANCELLED', 'ESTORNADO', 'REFUNDED'].includes(status)) {
          this.pararMonitoramentoPagamento();
        }

        this.cdr.detectChanges();
      });
  }

  get maximoParcelas(): number {
    if (!this.planoSelecionado) return 1;

    return Math.min(Math.max(this.planoSelecionado.intervaloMeses, 1), 12);
  }

  get opcoesParcelamento(): number[] {
    return Array.from({ length: this.maximoParcelas }, (_, indice) => indice + 1);
  }

  calcularTotalCartao(parcelas: number): number {
    if (!this.planoSelecionado) return 0;

    const valor = this.planoSelecionado.valor;
    const percentual = parcelas <= 6 ? 0.0349 : 0.0399;
    const taxaFixa = 0.49;

    return Math.round((valor + valor * percentual + taxaFixa) * 100) / 100;
  }

  calcularValorParcela(parcelas: number): number {
    if (parcelas < 1) return 0;

    return this.calcularTotalCartao(parcelas) / parcelas;
  }

  get valorTotalEstimado(): number {
    if (!this.planoSelecionado) return 0;

    if (this.formaPagamento === 'PIX') {
      return this.planoSelecionado.valor;
    }

    return this.calcularTotalCartao(this.quantidadeParcelas);
  }

  get taxaEstimada(): number {
    if (this.formaPagamento === 'PIX') {
      return 0;
    }

    return Math.round((this.valorTotalEstimado - (this.planoSelecionado?.valor ?? 0)) * 100) / 100;
  }

  async carregarPlanos(): Promise<void> {
    this.carregandoPlanos = true;
    this.mensagemErro = '';

    try {
      const resposta = await firstValueFrom(
        this.http.get<unknown>(`${this.apiBaseUrl}/planos`).pipe(timeout({ first: 15000 })),
      );

      let lista: unknown[] = [];

      if (Array.isArray(resposta)) {
        lista = resposta;
      } else if (resposta && typeof resposta === 'object') {
        const objeto = resposta as Record<string, unknown>;

        if (Array.isArray(objeto['planos'])) {
          lista = objeto['planos'];
        } else if (Array.isArray(objeto['data'])) {
          lista = objeto['data'];
        } else if (Array.isArray(objeto['content'])) {
          lista = objeto['content'];
        }
      }

      this.planos = lista
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => ({
          codigo: String(item['codigo'] ?? ''),
          nome: String(item['nome'] ?? 'Premium'),
          valor: Number(item['valor'] ?? 0),
          intervaloMeses: Number(item['intervaloMeses'] ?? 0),
        }))
        .filter((plano) => plano.codigo.length > 0 && plano.valor > 0 && plano.intervaloMeses > 0)
        .sort((a, b) => a.intervaloMeses - b.intervaloMeses);

      if (this.planos.length === 0) {
        this.mensagemErro = 'O servidor respondeu, mas não retornou planos válidos.';
        return;
      }

      this.planoSelecionado =
        this.planos.find((plano) => plano.intervaloMeses === 12) ?? this.planos[0];
    } catch (erro: unknown) {
      console.error('Erro ao carregar planos:', erro);
      this.mensagemErro = this.extrairMensagemErro(
        erro,
        'Não foi possível carregar os planos. Verifique a conexão com o servidor e tente novamente.',
      );
    } finally {
      this.carregandoPlanos = false;
      this.cdr.detectChanges();
    }
  }

  selecionarPlano(plano: PlanoCheckout): void {
    this.planoSelecionado = plano;

    if (plano.codigo === 'PREMIUM_MENSAL') {
      // Mensal recorrente: somente cartão e uma cobrança por mês.
      this.formaPagamento = 'CARTAO';
      this.quantidadeParcelas = 1;

      // Exige nova autorização ao selecionar o mensal.
      this.recorrenciaAceita = false;
    } else {
      this.recorrenciaAceita = false;
    }

    if (this.quantidadeParcelas > this.maximoParcelas) {
      this.quantidadeParcelas = this.maximoParcelas;
    }

    this.limparResultadosPagamento();
    this.mensagemErro = '';
    this.mensagemSucesso = '';
  }

  selecionarPagamento(forma: FormaPagamento): void {

    // Impede Pix no plano mensal recorrente.
    if (
      this.planoSelecionado?.codigo === 'PREMIUM_MENSAL' &&
      forma === 'PIX'
    ) {
      this.formaPagamento = 'CARTAO';
      this.quantidadeParcelas = 1;

      this.mensagemErro =
        'O plano mensal recorrente aceita somente cartão de crédito.';

      return;
    }

    // Atualiza a forma de pagamento somente após validar.
    this.formaPagamento = forma;

    this.limparResultadosPagamento();
    this.mensagemErro = '';
    this.mensagemSucesso = '';

    if (forma === 'PIX') {
      this.quantidadeParcelas = 1;
    }

    // Garante que o mensal nunca seja parcelado.
    if (this.planoSelecionado?.codigo === 'PREMIUM_MENSAL') {
      this.quantidadeParcelas = 1;
    }
  }

  nomePlano(plano: PlanoCheckout): string {
    if (plano.intervaloMeses === 12) return 'Plano Anual';
    if (plano.intervaloMeses === 6) return 'Plano Semestral';
    if (plano.intervaloMeses === 3) return 'Plano Trimestral';
    if (plano.intervaloMeses === 1) return 'Plano Mensal';

    return plano.nome;
  }

  formatarMoeda(valor: number | string | null | undefined): string {
    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
      return '—';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numero);
  }

  traduzirStatusPagamento(status?: string | null): string {
    const situacao = (status ?? '').trim().toUpperCase();

    const traducoes: Record<string, string> = {
      PENDING: 'Aguardando pagamento',
      AWAITING_PAYMENT: 'Aguardando pagamento',
      CONFIRMED: 'Pagamento confirmado',
      RECEIVED: 'Pagamento recebido',
      OVERDUE: 'Pagamento vencido',
      REFUNDED: 'Pagamento estornado',
      REFUND_REQUESTED: 'Estorno solicitado',
      REFUND_IN_PROGRESS: 'Estorno em andamento',
      CANCELED: 'Pagamento cancelado',
      CANCELLED: 'Pagamento cancelado',
      DELETED: 'Cobrança removida',
      FAILED: 'Falha no pagamento',
      EXPIRED: 'Pix expirado',
      AUTHORIZED: 'Pagamento autorizado',
      IN_ANALYSIS: 'Pagamento em análise',
      PENDENTE: 'Aguardando pagamento',
      PAGO: 'Pagamento confirmado',
      VENCIDO: 'Pagamento vencido',
      ESTORNADO: 'Pagamento estornado',
      CANCELADO: 'Pagamento cancelado',
    };

    return traducoes[situacao] ?? 'Verificando pagamento';
  }

  aplicarMascaraTelefone(valor: string): string {
    const numeros = String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 11);

    if (numeros.length <= 2) {
      return numeros.length ? `(${numeros}` : '';
    }

    if (numeros.length <= 7) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    }

    if (numeros.length <= 10) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
    }

    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
  }

  aplicarMascaraCpf(valor: string): string {
    const numeros = String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 11);

    return numeros
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  }

  aplicarMascaraCep(valor: string): string {
    const numeros = String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 8);

    return numeros.length > 5 ? `${numeros.slice(0, 5)}-${numeros.slice(5)}` : numeros;
  }

  aplicarMascaraValidade(valor: string): string {
    const numeros = String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 6);

    if (numeros.length <= 2) {
      return numeros;
    }

    return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
  }

  async buscarCep(): Promise<void> {
    const cep = this.endereco.cep.replace(/\D/g, '');

    if (cep.length !== 8) {
      return;
    }

    this.buscandoCep = true;

    try {
      const resposta = await firstValueFrom(
        this.http
          .get<{
            erro?: boolean;
            logradouro?: string;
            bairro?: string;
            localidade?: string;
            uf?: string;
          }>(`https://viacep.com.br/ws/${cep}/json/`)
          .pipe(timeout({ first: 10000 })),
      );

      if (resposta.erro) {
        this.mensagemErro = 'CEP não encontrado. Confira o número informado.';
        return;
      }

      this.endereco.logradouro = resposta.logradouro ?? this.endereco.logradouro;
      this.endereco.bairro = resposta.bairro ?? this.endereco.bairro;
      this.endereco.cidade = resposta.localidade ?? this.endereco.cidade;
      this.endereco.uf = resposta.uf ?? this.endereco.uf;

      this.mensagemErro = '';
    } catch (erro: unknown) {
      console.error('Erro ao consultar CEP:', erro);

      this.mensagemErro =
        'Não foi possível consultar o CEP. Você pode preencher o endereço manualmente.';
    } finally {
      this.buscandoCep = false;
      this.cdr.detectChanges();
    }
  }

  enviarFormulario(form: NgForm): void {
    void this.finalizarCompra(form);
  }

  async finalizarCompra(form: NgForm): Promise<void> {
    this.mensagemErro = '';
    this.mensagemSucesso = '';

    if (this.processando) {
      return;
    }

    if (!this.planoSelecionado) {
      this.mensagemErro = 'Selecione um plano antes de continuar.';
      return;
    }

    const planoMensal =
      this.planoSelecionado.codigo === 'PREMIUM_MENSAL';

    // Segurança: mensal recorrente somente no cartão.
    if (planoMensal) {
      this.formaPagamento = 'CARTAO';
      this.quantidadeParcelas = 1;

      if (!this.recorrenciaAceita) {
        this.mensagemErro =
          'Para contratar o plano mensal, autorize a cobrança automática recorrente.';
        return;
      }
    }

    if (form.invalid) {
      form.control.markAllAsTouched();

      this.mensagemErro =
        'Confira os campos obrigatórios e aceite os termos e a política de privacidade.';
      return;
    }

    if (!this.validarCpf(this.comprador.cpf)) {
      this.mensagemErro =
        'Informe um CPF válido para quem está contratando.';
      return;
    }

    if (
      this.formaPagamento === 'CARTAO' &&
      !this.validarCpfCnpj(this.cartao.cpfCnpjTitular)
    ) {
      this.mensagemErro =
        'Informe um CPF ou CNPJ válido para o titular do cartão.';
      return;
    }

    if (!this.validarCartao()) {
      return;
    }

    if (
      !this.validarTelefone(this.comprador.telefone) ||
      !this.validarTelefone(this.usuario.telefone) ||
      (
        !this.compradorEhEmergencia &&
        !this.validarTelefone(this.emergencia.telefone)
      )
    ) {
      this.mensagemErro =
        'Confira os telefones informados. Inclua o DDD.';
      return;
    }

    if (!this.validarEmail(this.comprador.email)) {
      this.mensagemErro =
        'Informe um e-mail válido para quem está contratando.';
      return;
    }

    this.processando = true;
    this.abrirCarregamentoPagamento();
    this.limparResultadosPagamento();
    this.cdr.detectChanges();

    try {
      const emergenciaPayload = this.compradorEhEmergencia
        ? {
          nome: this.comprador.nome.trim(),
          telefone: this.comprador.telefone.replace(/\D/g, ''),
          email: this.comprador.email.trim(),
        }
        : {
          nome: this.emergencia.nome.trim(),
          telefone: this.emergencia.telefone.replace(/\D/g, ''),
          email: this.emergencia.email.trim(),
        };

      const pedidoPayload = {
        comprador: {
          nome: this.comprador.nome.trim(),
          telefone: this.comprador.telefone.replace(/\D/g, ''),
          email: this.comprador.email.trim(),
          cpf: this.comprador.cpf.replace(/\D/g, ''),
          vinculoUsuario: this.comprador.vinculoUsuario.trim(),
        },

        usuario: {
          nome: this.usuario.nome.trim(),
          telefone: this.usuario.telefone.replace(/\D/g, ''),
          email: this.usuario.email.trim(),
          dataNascimento: this.usuario.dataNascimento,
          possuiLimitacaoFisica: this.usuario.possuiLimitacaoFisica,
          informacaoMobilidade: this.usuario.possuiLimitacaoFisica
            ? this.usuario.informacaoMobilidade.trim()
            : '',
        },

        emergencia: emergenciaPayload,

        cep: this.endereco.cep.replace(/\D/g, ''),
        logradouro: this.endereco.logradouro.trim(),
        numero: this.endereco.numero.trim(),
        complemento: this.endereco.complemento.trim(),
        bairro: this.endereco.bairro.trim(),
        cidade: this.endereco.cidade.trim(),
        uf: this.endereco.uf,

        planoCodigo: this.planoSelecionado.codigo,
        formaPagamento: this.formaPagamento,

        // Plano mensal não permite parcelamento.
        quantidadeParcelas:
          planoMensal || this.formaPagamento === 'PIX'
            ? 1
            : this.quantidadeParcelas,

        termosAceitos: this.termosAceitos,
        privacidadeAceita: this.privacidadeAceita,

        // O DTO do backend precisa aceitar este campo.
        recorrenciaAceita: planoMensal && this.recorrenciaAceita,
      };

      const pedido = await firstValueFrom(
        this.http
          .post<PedidoCriado>(
            `${this.apiBaseUrl}/pedidos`,
            pedidoPayload
          )
          .pipe(timeout({ first: 30000 }))
      );

      if (
        pedido?.id === undefined ||
        pedido?.id === null ||
        pedido.id === ''
      ) {
        throw new Error(
          'O backend não retornou o identificador do pedido.'
        );
      }

      if (this.formaPagamento === 'PIX') {
        await this.iniciarPix(pedido.id);
      } else {
        await this.iniciarCartao(pedido.id);
      }

      this.iniciarMonitoramentoPagamento(pedido.id);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

    } catch (erro: unknown) {
      // Evite registrar objetos que possam conter dados do cartão.
      console.error('Erro ao finalizar checkout.');

      this.mensagemErro = this.extrairMensagemErro(erro);

    } finally {
      this.processando = false;
      this.fecharCarregamentoPagamento();

      // Remove dados sensíveis após a tentativa.
      this.cartao.numero = '';
      this.cartao.cvv = '';
      this.cartao.validade = '';

      this.cdr.detectChanges();
    }
  }

  private async iniciarPix(pedidoId: number | string): Promise<void> {
    const resposta = await firstValueFrom(
      this.http
        .post<unknown>(`${this.apiBaseUrl}/pedidos/${encodeURIComponent(String(pedidoId))}/pix`, {})
        .pipe(timeout({ first: 30000 })),
    );

    if (!resposta || typeof resposta !== 'object') {
      throw new Error('O backend não retornou os dados do pagamento Pix.');
    }

    const dados = resposta as Record<string, unknown>;

    // Nomes reais retornados pelo backend, seguidos dos aliases.
    const imagem =
      dados['imagemQrCodeBase64'] ??
      dados['imagemBase64'] ??
      dados['encodedImage'] ??
      dados['qrCodeImage'] ??
      dados['imagem'] ??
      '';

    const codigo =
      dados['codigoPix'] ??
      dados['copiaECola'] ??
      dados['payload'] ??
      dados['qrCode'] ??
      dados['pixCopiaECola'] ??
      '';

    const paymentId = dados['paymentId'] ?? dados['asaasPaymentId'] ?? dados['id'];

    const status = dados['status'] ?? 'PENDING';

    const expiraEm = dados['expiraEm'] ?? dados['expirationDate'] ?? dados['expiresAt'];

    const imagemFinal = typeof imagem === 'string' ? imagem.trim() : '';

    const codigoFinal = typeof codigo === 'string' ? codigo.trim() : '';

    if (!imagemFinal && !codigoFinal) {
      console.error('Resposta recebida ao gerar Pix:', dados);

      throw new Error(
        'A API não retornou o QR Code nem o código Pix. Confira a resposta do backend.',
      );
    }

    this.resultadoPix = {
      paymentId: paymentId !== null && paymentId !== undefined ? String(paymentId) : undefined,
      status: String(status),
      imagemBase64: imagemFinal,
      copiaECola: codigoFinal,
      expiraEm: expiraEm ? String(expiraEm) : undefined,
    };

    this.pixCopiado = false;

    this.mensagemSucesso = 'Pedido criado. Conclua o pagamento usando os dados Pix abaixo.';

    this.cdr.detectChanges();
  }

  private async iniciarCartao(pedidoId: number | string): Promise<void> {
    const partesValidade = this.cartao.validade.split('/');
    const mesValidade = (partesValidade[0] ?? '').trim();
    const anoValidade = (partesValidade[1] ?? '').trim();

    if (!mesValidade || !/^\d{2}$/.test(mesValidade)) {
      throw new Error('Informe a validade do cartão no formato MM/AAAA.');
    }

    if (!anoValidade || !/^\d{4}$/.test(anoValidade)) {
      throw new Error('O ano de validade deve ter quatro dígitos.');
    }

    const ip = await this.obterIpPublico();

    const cartaoPayload = {
      titular: this.cartao.titular.trim(),
      numero: this.cartao.numero.replace(/\D/g, ''),
      mesValidade,
      anoValidade,
      cvv: this.cartao.cvv.trim(),
      emailTitular: this.comprador.email.trim(),
      cpfCnpjTitular: this.cartao.cpfCnpjTitular.replace(/\D/g, ''),
      cep: this.endereco.cep.replace(/\D/g, ''),
      numeroEndereco: this.endereco.numero.trim(),
      telefone: this.comprador.telefone.replace(/\D/g, ''),
      ip,

      endereco: {
        rua: this.endereco.logradouro.trim(),
        numero: this.endereco.numero.trim(),
        complemento: this.endereco.complemento.trim(),
        bairro: this.endereco.bairro.trim(),
        cidade: this.endereco.cidade.trim(),
        estado: this.endereco.uf.trim().toUpperCase(),
      },
    };

    const resposta = await firstValueFrom(
      this.http
        .post<PagamentoCartao>(
          `${this.apiBaseUrl}/pedidos/${encodeURIComponent(String(pedidoId))}/cartao`,
          cartaoPayload,
        )
        .pipe(timeout({ first: 30000 })),
    );

    if (!resposta?.paymentId) {
      console.error('Resposta do backend sem paymentId:', {
        status: resposta?.status,
        invoiceUrl: resposta?.invoiceUrl,
      });

      throw new Error(
        'O servidor não retornou o identificador da cobrança. Confira os logs do backend.',
      );
    }

    this.resultadoCartao = {
      paymentId: resposta.paymentId,
      status: resposta.status ?? 'PENDING',
      invoiceUrl: resposta.invoiceUrl,
    };

    this.mensagemSucesso =
      'Cobrança criada. Confira o status do pagamento para concluir sua assinatura.';

    this.cdr.detectChanges();
  }

  private async obterIpPublico(): Promise<string> {
    try {
      const resposta = await firstValueFrom(
        this.http
          .get<{ ip: string }>('https://api.ipify.org?format=json')
          .pipe(timeout({ first: 10000 })),
      );

      if (!resposta?.ip) {
        throw new Error('IP não retornado.');
      }

      return resposta.ip;
    } catch (erro: unknown) {
      console.error('Erro ao obter IP público:', erro);

      throw new Error(
        'Não foi possível obter o IP necessário para processar o cartão. Tente novamente.',
      );
    }
  }

  get imagemQrCode(): string {
    const imagem = this.resultadoPix?.imagemBase64?.trim() ?? '';

    if (!imagem) {
      return '';
    }

    if (imagem.startsWith('data:image/')) {
      return imagem;
    }

    return `data:image/png;base64,${imagem}`;
  }

  async copiarPix(): Promise<void> {
    const codigo = this.resultadoPix?.copiaECola;

    if (!codigo) {
      this.mensagemErro = 'Não há código Pix disponível para copiar.';
      return;
    }

    try {
      await navigator.clipboard.writeText(codigo);
      this.pixCopiado = true;

      await this.mostrarSucesso(
        'Código copiado!',
        'Abra o aplicativo do seu banco e cole o código Pix para concluir o pagamento.',
      );
    } catch (erro: unknown) {
      console.error('Erro ao copiar Pix:', erro);

      this.mensagemErro =
        'Não foi possível copiar automaticamente. Selecione e copie o código Pix manualmente.';
    }
  }

  private validarCartao(): boolean {
    if (this.formaPagamento !== 'CARTAO') {
      return true;
    }

    const numero = this.cartao.numero.replace(/\D/g, '');
    const cvv = this.cartao.cvv.replace(/\D/g, '');
    const [mes, ano] = this.cartao.validade.split('/');

    if (numero.length < 13 || numero.length > 19) {
      this.mensagemErro = 'Confira o número do cartão.';
      return false;
    }

    if (!this.cartao.titular.trim()) {
      this.mensagemErro = 'Informe o nome impresso no cartão.';
      return false;
    }

    if (!mes || !ano || !/^\d{2}$/.test(mes) || !/^\d{4}$/.test(ano)) {
      this.mensagemErro = 'Informe a validade no formato MM/AAAA.';
      return false;
    }

    const mesNumero = Number(mes);
    const anoNumero = Number(ano);
    const agora = new Date();
    const anoAtual = agora.getFullYear();
    const mesAtual = agora.getMonth() + 1;

    if (
      mesNumero < 1 ||
      mesNumero > 12 ||
      anoNumero < anoAtual ||
      (anoNumero === anoAtual && mesNumero < mesAtual)
    ) {
      this.mensagemErro = 'O cartão está com uma validade inválida ou vencida.';
      return false;
    }

    if (cvv.length < 3 || cvv.length > 4) {
      this.mensagemErro = 'Confira o código de segurança do cartão.';
      return false;
    }

    if (
      !Number.isInteger(this.quantidadeParcelas) ||
      this.quantidadeParcelas < 1 ||
      this.quantidadeParcelas > this.maximoParcelas
    ) {
      this.mensagemErro = `Este plano permite parcelamento de 1 a ${this.maximoParcelas} vezes.`;

      return false;
    }

    return true;
  }

  aplicarMascaraCartao(valor: string): string {
    const numeros = String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 19);

    return numeros.match(/.{1,4}/g)?.join(' ') ?? '';
  }

  aplicarMascaraCvv(valor: string): string {
    return String(valor ?? '')
      .replace(/\D/g, '')
      .slice(0, 4);
  }

  private validarCpf(valor: string): boolean {
    const cpf = String(valor ?? '').replace(/\D/g, '');

    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
      return false;
    }

    let soma = 0;

    for (let i = 0; i < 9; i++) {
      soma += Number(cpf[i]) * (10 - i);
    }

    let digito = (soma * 10) % 11;

    if (digito === 10) {
      digito = 0;
    }

    if (digito !== Number(cpf[9])) {
      return false;
    }

    soma = 0;

    for (let i = 0; i < 10; i++) {
      soma += Number(cpf[i]) * (11 - i);
    }

    digito = (soma * 10) % 11;

    if (digito === 10) {
      digito = 0;
    }

    return digito === Number(cpf[10]);
  }

  private validarCnpj(valor: string): boolean {
    const cnpj = String(valor ?? '').replace(/\D/g, '');

    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
      return false;
    }

    const calcularDigito = (base: string, pesos: number[]): number => {
      const soma = base
        .split('')
        .reduce((total, digito, indice) => total + Number(digito) * pesos[indice], 0);

      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const primeiro = calcularDigito(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    const segundo = calcularDigito(
      cnpj.slice(0, 12) + String(primeiro),
      [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
    );

    return primeiro === Number(cnpj[12]) && segundo === Number(cnpj[13]);
  }

  private validarCpfCnpj(valor: string): boolean {
    const documento = String(valor ?? '').replace(/\D/g, '');

    if (documento.length === 11) {
      return this.validarCpf(documento);
    }

    if (documento.length === 14) {
      return this.validarCnpj(documento);
    }

    return false;
  }

  private validarTelefone(valor: string): boolean {
    const numeros = String(valor ?? '').replace(/\D/g, '');

    return numeros.length === 10 || numeros.length === 11;
  }

  private validarEmail(valor: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(valor ?? '').trim());
  }

  private extrairMensagemErro(erro: unknown, mensagemPadrao?: string): string {
    const httpErro = erro as {
      error?: {
        message?: string;
        mensagem?: string;
        detail?: string;
        title?: string;
      };
      message?: string;
      status?: number;
    };

    const mensagem =
      httpErro?.error?.message ??
      httpErro?.error?.mensagem ??
      httpErro?.error?.detail ??
      httpErro?.error?.title;

    if (mensagem) {
      return mensagem;
    }

    if (httpErro?.status === 0 || httpErro?.message?.includes('0 Unknown Error')) {
      return 'Não foi possível conectar ao backend. Verifique a URL da API, o CORS e se o servidor está em execução.';
    }

    if (httpErro?.status === 400) {
      return 'Os dados enviados foram recusados pelo servidor. Confira as informações preenchidas.';
    }

    if (httpErro?.status === 401 || httpErro?.status === 403) {
      return 'O servidor não autorizou a solicitação. Confira a configuração do checkout.';
    }

    if (httpErro?.status === 404) {
      return 'A rota solicitada não foi encontrada no backend. Confira os endpoints do checkout.';
    }

    if (httpErro?.status === 408) {
      return 'A solicitação demorou demais. Tente novamente.';
    }

    if (httpErro?.status && httpErro.status >= 500) {
      return 'Ocorreu um erro no servidor ao processar sua solicitação. Tente novamente mais tarde.';
    }

    return (
      httpErro?.message ??
      mensagemPadrao ??
      'Não foi possível concluir o checkout. Confira os dados e tente novamente.'
    );
  }

  private limparResultadosPagamento(): void {
    this.resultadoPix = null;
    this.resultadoCartao = null;
    this.pixCopiado = false;
    this.pagamentoConfirmado = false;
  }
}
